import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { Box, Chip, Typography, IconButton, Tooltip, Alert, Snackbar, CircularProgress } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CategoryIcon from '@mui/icons-material/Category';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import Datagrid from '../../components/Datagrid/Datagrid';
import DatagridLoading from '../../components/DatagridLoading/DatagridLoading';
import DocCardImage from '../DocCardImage/DocCardImage';
import {
    addFavorite,
    deleteDocument,
    deleteDocumentsByMachineNumber,
    getDocumentCategoryList,
    reassignDocumentsToCategory,
    reassignDocumentsToMachine,
    removeFavorite,
    searchMachines,
    trackDocumentView,
    uploadAdminPortalDocument,
} from '../../services/documentService';
import DocumentViewModal from '../DocumentViewModal/DocumentViewModal';
import * as locales from '@mui/x-data-grid/locales';
import { downloadDocumentProxy, exportHtmlManualPdf } from '../../services/documentService';
import { toast } from '../../context/ToastContext';
import AdminCategoryUpdateModal from './AdminCategoryUpdateModal';
import AdminDocumentUploadModal from './AdminDocumentUploadModal';
import AdminMachineReassignModal from './AdminMachineReassignModal';
import {
    DOCUMENT_TAB_CATEGORIES,
    KNOWN_DOCUMENT_CATEGORIES,
    getDocumentCategoryLabel,
    normalizeDocumentCategoryKey,
} from '../../utils/documentCategories';
import { trackEvent } from '../../utilities/analyticsService';

// Configuration
const MAX_BULK_DOWNLOAD = 10;

// Helper function to get document category
const getDocumentCategory = (doc) => {
    let category = 'uncategorized';

    if (doc.categories && Array.isArray(doc.categories) && doc.categories.length > 0) {
        category = doc.categories[0].category_name || doc.categories[0].label || 'uncategorized';
    } else if (doc.category) {
        category = doc.category;
    }

    const normalized = normalizeDocumentCategoryKey(category);

    return normalized;
};

const isHtmlManual = (doc) => {
    if (getDocumentCategory(doc) !== 'manual') return false;
    return [doc?.name, doc?.blob_path, doc?.attachment].some((value) =>
        /\.html?(?:[?#].*)?$/i.test(String(value || '').trim())
    );
};

const getDocumentDownloadFilename = (doc, fallback = 'document') => {
    const filename = doc?.name || fallback;
    return isHtmlManual(doc) ? `${filename.replace(/\.html?$/i, '') || 'Manual'}.pdf` : filename;
};

const getDocumentDownloadBlob = (doc) => {
    if (isHtmlManual(doc)) {
        const blobPath = doc?.blob_path || doc?.attachment;
        if (!blobPath) throw new Error('HTML manual path is unavailable');
        return exportHtmlManualPdf(blobPath);
    }
    return downloadDocumentProxy(doc.id);
};

const getDocumentCategorySource = (doc, currentCategory = null) => {
    const categories = Array.isArray(doc?.categories) ? doc.categories : [];
    const normalizedCurrentCategory = currentCategory ? normalizeDocumentCategoryKey(currentCategory) : null;

    if (normalizedCurrentCategory && normalizedCurrentCategory !== 'others') {
        const matchedCategory = categories.find((category) => {
            const categoryName = category?.category_name || category?.label || '';
            return normalizeDocumentCategoryKey(categoryName) === normalizedCurrentCategory;
        });

        if (matchedCategory) {
            return {
                id: matchedCategory.category_id || matchedCategory.id || null,
                name: matchedCategory.category_name || matchedCategory.label || currentCategory,
                key: normalizedCurrentCategory,
            };
        }
    }

    if (categories.length > 0) {
        const primaryCategory = categories[0];
        const categoryName = primaryCategory?.category_name || primaryCategory?.label || doc?.category || 'Uncategorized';
        return {
            id: primaryCategory.category_id || primaryCategory.id || null,
            name: categoryName,
            key: normalizeDocumentCategoryKey(categoryName),
        };
    }

    const fallbackName = doc?.category || 'Uncategorized';
    return {
        id: null,
        name: fallbackName,
        key: normalizeDocumentCategoryKey(fallbackName),
    };
};

// Fires the analytics event the User Analytics "Document Downloads" table is built from -
// the generic click-based file_downloaded tracker never sees the document's category, so this
// is the only place that download is attributed to a document type (Manual, BOM, etc).
const trackDocumentDownloadEvent = (doc) => {
    const categorySource = getDocumentCategorySource(doc);
    const primaryMachine = Array.isArray(doc?.machine_details) ? doc.machine_details[0] || {} : {};
    trackEvent("document_download_detail", {
        document_id: doc?.id,
        document_name: doc?.name || doc?.document_name || "",
        document_category: categorySource.name,
        machine_id: doc?.machine_id || primaryMachine.machine_id || "",
        machine_name: doc?.machine_name || primaryMachine.machine_name || "",
        machine_number: doc?.machine_number || primaryMachine.machine_number || "",
        action_category: "documents",
        element_label: `Download document ${doc?.name || doc?.id}`,
    });
};

const DocumentationTable = ({
    documents = [],
    favorites = [],
    selectedDocuments = [],
    onToggleFavorite,
    onSelectDocument,
    onFavoriteToggled,
    loading = false,
    selectedSiteId,
    currentCategory = null,
    userRole = null,
    canAddDocument = false,
    canUpdateCategory = false,
    canChangeMachine = false,
    canDeleteDocuments = false,
    bulkDeleteMachineNumber = null,
    canDownloadSelectedDocuments = true,
    onCategoriesUpdated = null,
    onClearFavoritesFilter = null,  // NEW: Add callback to clear favorites filter
    hideFavoriteIcon = false,
    // A specific document to auto-open in the view modal, exactly as if the user had
    // clicked it (used by the page-level ?document_id= deep-link effect in
    // Documentation.js, e.g. when CHIP navigates here to open one named document).
    documentToOpen = null,
    onDocumentOpened = null,
    // NEW PAGINATION PROPS:
    rowCount = 0,                      // Total rows from backend
    paginationModel = { page: 0, pageSize: 10 },  // Current pagination state
    onPaginationModelChange,           // Callback when page/size changes
    paginationMode = "client"
}) => {
    const { t, i18n } = useTranslation();
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
    const [filteredRows, setFilteredRows] = useState(documents);
    const [downloading, setDownloading] = useState(false);
    const [singleDownloading, setSingleDownloading] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    // Modal state
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [activeDocument, setActiveDocument] = useState(null);
    const [selectedDocsCache, setSelectedDocsCache] = useState(new Map());
    const [categoryModalOpen, setCategoryModalOpen] = useState(false);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(false);
    const [categoriesReady, setCategoriesReady] = useState(false);
    const [categorySubmitting, setCategorySubmitting] = useState(false);
    const [targetCategory, setTargetCategory] = useState(null);
    const [deleteSubmitting, setDeleteSubmitting] = useState(false);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [uploadSubmitting, setUploadSubmitting] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [uploadFormData, setUploadFormData] = useState({ files: [] });
    const [uploadFormResetKey, setUploadFormResetKey] = useState(0);
    const [uploadSelectedMachine, setUploadSelectedMachine] = useState(null);
    const [uploadSelectedCategory, setUploadSelectedCategory] = useState(null);
    const [machineReassignModalOpen, setMachineReassignModalOpen] = useState(false);
    const [machineReassignSubmitting, setMachineReassignSubmitting] = useState(false);
    const [machineReassignError, setMachineReassignError] = useState('');
    const [machineOptions, setMachineOptions] = useState([]);
    const [machineOptionsLoading, setMachineOptionsLoading] = useState(false);
    const [targetMachineSelection, setTargetMachineSelection] = useState(null);
    const machineSearchTimeoutRef = useRef(null);
    const categorySearchTimeoutRef = useRef(null);

    // updates cache whenever page changes or selection changes
    useEffect(() => {
        setSelectedDocsCache(prev => {
            const next = new Map(prev);
            // Add docs from current page that are selected
            documents.forEach(doc => {
                if (selectedDocuments.includes(doc.id)) {
                    next.set(doc.id, doc);
                }
            });
            // Remove docs that were deselected
            next.forEach((_, id) => {
                if (!selectedDocuments.includes(id)) {
                    next.delete(id);
                }
            });
            return next;
        });
    }, [documents, selectedDocuments]);

    //reads from cache so off-page docs are included
    const selectedDocsData = useMemo(() => {
        return selectedDocuments.map(id => selectedDocsCache.get(id)).filter(Boolean);
    }, [selectedDocuments, selectedDocsCache]);

    const selectedCategorySource = useMemo(() => {
        if (selectedDocsData.length === 0) return null;
        return getDocumentCategorySource(selectedDocsData[0], currentCategory);
    }, [selectedDocsData, currentCategory]);

    const normalizeMachineOptions = useCallback((rows = []) => {
        return (Array.isArray(rows) ? rows : [])
            .map((machine) => {
                const machineId = Number(machine?.machine_id);
                if (!Number.isFinite(machineId) || machineId <= 0) return null;

                const machineNumber = `${machine?.machine_number || ''}`.trim();
                const machineName = `${machine?.machine_name || ''}`.trim();
                const label = [machineNumber, machineName].filter(Boolean).join(' - ') || `${machineId}`;

                return {
                    ...machine,
                    machine_id: machineId,
                    machine_number: machineNumber,
                    machine_name: machineName,
                    label,
                    machine_label: label,
                };
            })
            .filter(Boolean);
    }, []);

    const localeText = useMemo(() => {
        const muiLocaleMap = {
            en: 'enUS',
            fr: 'frFR',
            es: 'esES',
            de: 'deDE',
        };

        const currentLang = i18n.language?.split('-')[0] || 'en';
        const muiKey = muiLocaleMap[currentLang] || 'enUS';
        const muiInternalTexts = locales[muiKey]?.components?.MuiDataGrid?.defaultProps?.localeText;

        return {
            ...muiInternalTexts,
            noRowsLabel: t('MESSAGES.DOCUMENTS_NOT_AVAILABLE'),
            noResultsOverlayLabel: t('MESSAGES.NO_DOCUMENTS_FOUND'),
        };
    }, [t, i18n.language]);

    const areSameCategorySelected = useMemo(() => {
        if (selectedDocsData.length === 0) return true;
        const categories = [...new Set(
            selectedDocsData.map((doc) => getDocumentCategorySource(doc, currentCategory).key)
        )];
        return categories.length === 1;
    }, [selectedDocsData, currentCategory]);

    useEffect(() => {
        return () => {
            if (machineSearchTimeoutRef.current) {
                clearTimeout(machineSearchTimeoutRef.current);
            }
            if (categorySearchTimeoutRef.current) {
                clearTimeout(categorySearchTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (uploadError && Array.isArray(uploadFormData?.files) && uploadFormData.files.length > 0) {
            setUploadError('');
        }
    }, [uploadError, uploadFormData]);

    const showSnackbar = useCallback((message, severity = 'info') => {
        setSnackbar({ open: true, message, severity });
    }, []);

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const isOthersCategory = (category) => {
        if (!category) return true;

        const normalized = normalizeDocumentCategoryKey(category);

        // everything not known goes to others
        return !KNOWN_DOCUMENT_CATEGORIES.includes(normalized);
    };


    const handleColumnVisibilityChange = (newModel) => {
        const visibleColumns = columns.filter(col => {
            return newModel[col.field] !== false;
        });

        if (visibleColumns.length === 0) {
            console.warn('Cannot hide all columns. At least one column must remain visible.');
            return;
        }

        setColumnVisibilityModel(newModel);
    };

    const handleSelection = useCallback((selectedIds) => {
        const visibleIds = filteredRows.map(row => row.id);

        // IDs checked on the current page
        const currentPageSelected = selectedIds.filter(id => visibleIds.includes(id));

        // Preserve selections made on OTHER pages
        const otherPageSelected = selectedDocuments.filter(id => !visibleIds.includes(id));

        // Merge both
        let validSelectedIds = [...otherPageSelected, ...currentPageSelected];

        if (validSelectedIds.length === 0) {
            onSelectDocument([]);
            return;
        }

        const selectedDocs = documents.filter(doc => validSelectedIds.includes(doc.id));

        if (selectedDocs.length > 0) {
            const uniqueCategories = [...new Set(
                selectedDocs.map((doc) => getDocumentCategorySource(doc, currentCategory).key)
            )];
            if (uniqueCategories.length > 1) {
                showSnackbar(
                    t('MESSAGES.DIFFERENT_CATEGORY_WARNING') ||
                    'Cannot select documents from different categories.',
                    'warning'
                );
                return;
            }
        }

        if (validSelectedIds.length > MAX_BULK_DOWNLOAD) {
            showSnackbar(t('MESSAGES.MAX_SELECTION_LIMIT', { max: MAX_BULK_DOWNLOAD }), 'warning');
            validSelectedIds = validSelectedIds.slice(0, MAX_BULK_DOWNLOAD);
        }

        onSelectDocument(validSelectedIds);
    }, [filteredRows, documents, selectedDocuments, t, onSelectDocument, showSnackbar, currentCategory]);

    const handleSearch = ({ query, data }) => {
        if (!query || query.trim() === '') {
            setFilteredRows(data);
            return data;
        }

        const q = query.toLowerCase();
        const filtered = data.filter(row => {
            const name = (row.name || '').toLowerCase();
            const documentType = isOthersCategory(row.category) ? (row.document_type || '').toLowerCase() : '';
            const machineText = row.machine_details && row.machine_details.length > 0
                ? row.machine_details.map(m => `${m.machine_number} ${m.machine_name}`).join(' ').toLowerCase()
                : '';

            return name.includes(q) || documentType.includes(q) || machineText.includes(q);
        });

        setFilteredRows(filtered);
        return filtered;
    };

    useEffect(() => {
        setFilteredRows(documents);
    }, [documents]);

    const availableTargetCategories = useMemo(() => {
        const selectedSourceId = Number(selectedCategorySource?.id);
        const selectedSourceKey = normalizeDocumentCategoryKey(selectedCategorySource?.key || '');

        return (categoryOptions || []).filter((category) => {
            const categoryId = Number(category?.id || category?.category_id);
            const categoryName = normalizeDocumentCategoryKey(category?.key || category?.category_name || category?.label || '');

            if (Number.isFinite(selectedSourceId) && selectedSourceId > 0 && categoryId === selectedSourceId) {
                return false;
            }

            if (selectedSourceKey && categoryName === selectedSourceKey) {
                return false;
            }

            return true;
        });
    }, [categoryOptions, selectedCategorySource]);

    const normalizeCategoryList = useCallback((payload) => {
        if (!payload) return [];

        if (payload?.status === false) {
            return [];
        }

        const raw =
            Array.isArray(payload) ? payload :
            Array.isArray(payload?.data) ? payload.data :
            Array.isArray(payload?.data?.data) ? payload.data.data :
            [];

        const normalized = raw
            .map((item) => {
                if (!item) return null;
                const id = Number(item.id ?? item.category_id);
                const rawCategoryName = `${item.category_name ?? item.label ?? item.name ?? ""}`.trim();
                const categoryKey = normalizeDocumentCategoryKey(rawCategoryName);
                if (!rawCategoryName || !categoryKey) return null;
                const displayName = DOCUMENT_TAB_CATEGORIES.includes(categoryKey)
                    ? getDocumentCategoryLabel(t, categoryKey)
                    : rawCategoryName;
                return {
                    ...item,
                    id: Number.isFinite(id) ? id : item.id ?? item.category_id,
                    category_id: Number.isFinite(id) ? id : item.category_id,
                    category_name: rawCategoryName,
                    key: categoryKey,
                    label: displayName,
                    display_name: displayName,
                };
            })
            .filter(Boolean);

        return normalized;
    }, [t]);

    const handleLoadCategoriesForAssignment = useCallback(async (query = '', options = {}) => {
        try {
            const { resetTarget = true } = options;
            setCategoriesLoading(true);
            if (resetTarget) {
                setTargetCategory(null);
            }
            const response = await getDocumentCategoryList(query);
            if (response?.status === false) {
                throw new Error(response?.message || 'Failed to load categories');
            }

            const normalizedCategories = normalizeCategoryList(response);
            setCategoryOptions(normalizedCategories);
            setCategoriesReady(true);
        } catch (error) {
            setCategoryOptions([]);
            setCategoriesReady(false);
            showSnackbar(
                error?.response?.data?.message || 'Failed to load categories',
                'error'
            );
        } finally {
            setCategoriesLoading(false);
        }
    }, [normalizeCategoryList, showSnackbar]);

    const handleLoadMachineOptions = useCallback(async (query = '') => {
        try {
            setMachineOptionsLoading(true);
            const response = await searchMachines(query, 1, 50);
            if (response?.status === false) {
                throw new Error(response?.message || 'Failed to load machines');
            }
            setMachineOptions(normalizeMachineOptions(response?.data));
        } catch (error) {
            setMachineOptions([]);
            showSnackbar(
                error?.response?.data?.message || 'Failed to load machines',
                'error'
            );
        } finally {
            setMachineOptionsLoading(false);
        }
    }, [normalizeMachineOptions, showSnackbar]);

    const handleMachineSearchChange = useCallback((value = '') => {
        if (machineSearchTimeoutRef.current) {
            clearTimeout(machineSearchTimeoutRef.current);
        }

        machineSearchTimeoutRef.current = setTimeout(() => {
            handleLoadMachineOptions(value.trim());
        }, 300);
    }, [handleLoadMachineOptions]);

    const handleCategorySearchChange = useCallback((value = '') => {
        if (categorySearchTimeoutRef.current) {
            clearTimeout(categorySearchTimeoutRef.current);
        }

        categorySearchTimeoutRef.current = setTimeout(() => {
            handleLoadCategoriesForAssignment(value.trim(), { resetTarget: false });
        }, 300);
    }, [handleLoadCategoriesForAssignment]);

    const resetUploadFormState = useCallback(() => {
        setUploadFormData({ files: [] });
        setUploadFormResetKey((prev) => prev + 1);
        setUploadSelectedMachine(null);
        setUploadSelectedCategory(null);
        setUploadError('');
    }, []);

    const handleOpenUploadModal = useCallback(async () => {
        if (!canAddDocument) return;
        resetUploadFormState();
        setUploadModalOpen(true);
        await Promise.all([
            handleLoadMachineOptions(''),
            handleLoadCategoriesForAssignment('', { resetTarget: false }),
        ]);
    }, [canAddDocument, handleLoadCategoriesForAssignment, handleLoadMachineOptions, resetUploadFormState]);

    const handleCloseUploadModal = useCallback(() => {
        if (uploadSubmitting) return;
        setUploadModalOpen(false);
        resetUploadFormState();
    }, [resetUploadFormState, uploadSubmitting]);

    const handleSubmitUploadDocument = useCallback(async () => {
        const selectedFiles = Array.isArray(uploadFormData?.files)
            ? uploadFormData.files.filter(Boolean)
            : [];
        if (selectedFiles.length === 0) {
            setUploadError('Please select at least one file to upload');
            return;
        }

        if (!uploadSelectedMachine?.machine_id) {
            setUploadError('Please select a machine');
            return;
        }

        const categoryId = Number(uploadSelectedCategory?.id || uploadSelectedCategory?.category_id);
        const categoryLabel = `${uploadSelectedCategory?.category_name || uploadSelectedCategory?.label || ''}`.trim();
        if (!Number.isFinite(categoryId) || categoryId <= 0) {
            setUploadError('Please select a category from the database');
            return;
        }

        if (!categoryLabel) {
            setUploadError('Please select a category');
            return;
        }

        try {
            setUploadSubmitting(true);
            setUploadError('');

            const formData = new FormData();
            selectedFiles.forEach((file) => {
                formData.append('files', file);
            });
            formData.append('machine_id', uploadSelectedMachine.machine_id);
            formData.append('category_id', categoryId);
            formData.append('category_name', categoryLabel);

            const response = await uploadAdminPortalDocument(formData);
            const updatedCategory = response?.data?.category || uploadSelectedCategory;

            toast.trigger({
                message: response?.message || 'Document uploaded successfully',
                severity: 'success',
            });

            handleCloseUploadModal();
            if (typeof onCategoriesUpdated === 'function') {
                await onCategoriesUpdated(updatedCategory);
            }
        } catch (error) {
            setUploadError(
                error?.response?.data?.message || 'Failed to upload document'
            );
        } finally {
            setUploadSubmitting(false);
        }
    }, [
        handleCloseUploadModal,
        onCategoriesUpdated,
        uploadFormData,
        uploadSelectedCategory,
        uploadSelectedMachine,
    ]);

    const handleOpenMachineReassignModal = useCallback(async () => {
        if (!canChangeMachine || selectedDocsData.length === 0 || !areSameCategorySelected) {
            return;
        }

        setTargetMachineSelection(null);
        setMachineReassignError('');
        setMachineReassignModalOpen(true);
        await handleLoadMachineOptions('');
    }, [areSameCategorySelected, canChangeMachine, handleLoadMachineOptions, selectedDocsData.length]);

    const handleCloseMachineReassignModal = useCallback(() => {
        if (machineReassignSubmitting) return;
        setMachineReassignModalOpen(false);
        setTargetMachineSelection(null);
        setMachineReassignError('');
    }, [machineReassignSubmitting]);

    const handleSubmitMachineReassign = useCallback(async () => {
        if (!targetMachineSelection?.machine_id || selectedDocsData.length === 0) {
            setMachineReassignError('Please select a target machine');
            return;
        }

        try {
            setMachineReassignSubmitting(true);
            setMachineReassignError('');

            const response = await reassignDocumentsToMachine({
                document_ids: selectedDocsData.map((doc) => doc.id),
                target_machine_id: targetMachineSelection.machine_id,
            });

            toast.trigger({
                message: response?.message || 'Document machines updated successfully',
                severity: 'success',
            });

            onSelectDocument([]);
            setSelectedDocsCache(new Map());
            handleCloseMachineReassignModal();

            if (typeof onCategoriesUpdated === 'function') {
                await onCategoriesUpdated(selectedCategorySource);
            }
        } catch (error) {
            setMachineReassignError(
                error?.response?.data?.message || 'Failed to update document machine'
            );
        } finally {
            setMachineReassignSubmitting(false);
        }
    }, [
        handleCloseMachineReassignModal,
        onCategoriesUpdated,
        onSelectDocument,
        selectedCategorySource,
        selectedDocsData,
        targetMachineSelection,
    ]);

    const handleOpenCategoryModal = useCallback(() => {
        if (!canUpdateCategory || selectedDocsData.length === 0 || !areSameCategorySelected) {
            return;
        }
        setTargetCategory(null);
        setCategoryOptions([]);
        setCategoriesReady(false);
        setCategoryModalOpen(true);
    }, [canUpdateCategory, selectedDocsData.length, areSameCategorySelected]);

    const handleCloseCategoryModal = useCallback(() => {
        if (categorySubmitting) return;
        setCategoryModalOpen(false);
        setTargetCategory(null);
        setCategoryOptions([]);
        setCategoriesReady(false);
    }, [categorySubmitting]);

    const handleSubmitCategoryUpdate = useCallback(async () => {
        if (!targetCategory || selectedDocsData.length === 0 || !selectedCategorySource) {
            return;
        }

        try {
            setCategorySubmitting(true);
            const payload = {
                document_ids: selectedDocsData.map((doc) => doc.id),
                target_category_id: targetCategory.id || targetCategory.category_id,
            };

            if (selectedCategorySource?.id) {
                payload.source_category_id = selectedCategorySource.id;
            }
            if (selectedCategorySource?.name) {
                payload.source_category_name = selectedCategorySource.name;
            }

            const response = await reassignDocumentsToCategory(payload);
            const updatedTargetCategory = response?.data?.target_category || targetCategory;

            toast.trigger({
                message: response?.message || 'Category updated successfully',
                severity: 'success',
            });

            onSelectDocument([]);
            setCategoryModalOpen(false);
            setTargetCategory(null);

            if (typeof onCategoriesUpdated === 'function') {
                onCategoriesUpdated(updatedTargetCategory);
            }
        } catch (error) {
            showSnackbar(
                error?.response?.data?.message || 'Failed to update category',
                'error'
            );
        } finally {
            setCategorySubmitting(false);
        }
    }, [
        onCategoriesUpdated,
        onSelectDocument,
        selectedCategorySource,
        selectedDocsData,
        showSnackbar,
        targetCategory,
    ]);

    const handleDeleteSelectedDocuments = useCallback(async () => {
        if (!canDeleteDocuments || selectedDocsData.length === 0 || deleteSubmitting) {
            return;
        }

        const confirmation = await Swal.fire({
            title: t('ALERT_MESSAGES.ARE_YOU_SURE', { defaultValue: 'Are you sure?' }),
            text: t('MESSAGES.DELETE_SELECTED_DOCUMENTS_CONFIRM', {
                defaultValue: 'Delete the selected {{count}} document(s)? This action cannot be undone.',
                count: selectedDocsData.length,
            }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('BUTTON.DELETE_SELECTED', { defaultValue: 'Delete Selected' }),
            cancelButtonText: t('BUTTON.CANCEL', { defaultValue: 'Cancel' }),
        });

        if (!confirmation.isConfirmed) return;

        setDeleteSubmitting(true);

        let deletedCount = 0;
        let failedCount = 0;

        for (const document of selectedDocsData) {
            const documentId = Number(document?.id);
            if (!Number.isFinite(documentId) || documentId <= 0) {
                failedCount += 1;
                continue;
            }

            try {
                const response = await deleteDocument(documentId);
                if (response?.status === false) {
                    failedCount += 1;
                } else {
                    deletedCount += 1;
                }
            } catch (_) {
                failedCount += 1;
            }
        }

        setDeleteSubmitting(false);

        if (deletedCount > 0) {
            onSelectDocument([]);
            setSelectedDocsCache(new Map());
            if (typeof onCategoriesUpdated === 'function') {
                await onCategoriesUpdated(selectedCategorySource);
            }
        }

        if (failedCount === 0) {
            toast.trigger({
                message: t('MESSAGES.DELETE_SELECTED_DOCUMENTS_SUCCESS', {
                    defaultValue: 'Deleted {{count}} document(s).',
                    count: deletedCount,
                }),
                severity: 'success',
            });
            return;
        }

        toast.trigger({
            message: t('MESSAGES.DELETE_SELECTED_DOCUMENTS_PARTIAL', {
                defaultValue: 'Deleted {{success}} document(s). {{failed}} failed.',
                success: deletedCount,
                failed: failedCount,
            }),
            severity: failedCount === selectedDocsData.length ? 'error' : 'warning',
        });
    }, [
        canDeleteDocuments,
        deleteSubmitting,
        onCategoriesUpdated,
        onSelectDocument,
        selectedCategorySource,
        selectedDocsData,
        t,
    ]);

    const handleDeleteAllForMachine = useCallback(async () => {
        const machineNumber = `${bulkDeleteMachineNumber || ''}`.trim();
        if (!canDeleteDocuments || !machineNumber || deleteSubmitting) return;

        const confirmation = await Swal.fire({
            title: 'Delete all machine documents?',
            text: `This will permanently delete all documents linked to machine ${machineNumber} in the current site. This action cannot be undone.`,
            icon: 'warning',
            input: 'text',
            inputLabel: `Type ${machineNumber} to confirm`,
            inputPlaceholder: machineNumber,
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Delete all',
            inputValidator: (value) => value?.trim() === machineNumber
                ? undefined
                : `Enter ${machineNumber} exactly`,
        });
        if (!confirmation.isConfirmed) return;

        setDeleteSubmitting(true);
        try {
            const response = await deleteDocumentsByMachineNumber(machineNumber, selectedSiteId);
            const deletedCount = Number(response?.data?.deleted_count || 0);
            const failedCount = Array.isArray(response?.data?.failed_document_ids)
                ? response.data.failed_document_ids.length
                : 0;

            if (typeof onCategoriesUpdated === 'function') {
                await onCategoriesUpdated(selectedCategorySource);
            }

            toast.trigger({
                message: response?.message || `Deleted ${deletedCount} document(s).${failedCount ? ` ${failedCount} failed.` : ''}`,
                severity: failedCount ? 'warning' : 'success',
            });
        } catch (error) {
            toast.trigger({
                message: error?.response?.data?.message || 'Could not delete machine documents.',
                severity: 'error',
            });
        } finally {
            setDeleteSubmitting(false);
        }
    }, [
        bulkDeleteMachineNumber,
        canDeleteDocuments,
        deleteSubmitting,
        onCategoriesUpdated,
        selectedCategorySource,
        selectedSiteId,
    ]);

    const handleRemoveDocumentFromSelection = useCallback((docId) => {
        if (!docId) return;
        const normalizedId = Number(docId);
        const nextSelection = selectedDocuments.filter(
            (id) => Number(id) !== normalizedId
        );
        onSelectDocument(nextSelection);
    }, [onSelectDocument, selectedDocuments]);

    const handleToggleFavorite = async (docId, isFavorite, docSiteId = null) => {
        try {
            const targetSiteId = docSiteId || selectedSiteId;
            const documentRow = documents.find(d => d.id === docId) || {};

            if (isFavorite) {
                await removeFavorite(docId, targetSiteId);
            } else {
                await addFavorite(docId, targetSiteId);
                trackEvent("document_favorite_added", {
                    document_id: docId,
                    document_name: documentRow.name || "",
                    site_id: targetSiteId || "",
                    machine_id: documentRow.machine_id || "",
                    machine_name: documentRow.machine_name || "",
                    machine_number: documentRow.machine_number || "",
                    action_category: "documents",
                    element_label: `Favorite document ${documentRow.name || docId}`,
                });
            }

            onToggleFavorite(docId, isFavorite);

            if (onFavoriteToggled) {
                await onFavoriteToggled();
            }

            // NEW: Check if this was the last favorite and clear filter if needed
            if (isFavorite) {
                // After removing, check if there are any favorites left
                const remainingFavorites = favorites.filter(fav => fav !== docId);

    
                // If no favorites remaining and we have a callback, trigger it
                if (remainingFavorites.length === 0 && onClearFavoritesFilter) {
    
                    onClearFavoritesFilter();
                }
            }
        } catch (error) {
            console.error('Error toggling favorite:', error);
            showSnackbar(t('MESSAGES.ERROR_TOGGLE_FAVORITE'), 'error');
            if (onFavoriteToggled) {
                onFavoriteToggled();
            }
        }
    };

    const handleTrackView = async (docId) => {
        try {
            const siteIdToUse = selectedSiteId || documents.find(d => d.id === docId)?.site_id;

            if (!siteIdToUse) {
                console.warn('Cannot track view - no siteId available');
                return false;
            }

            await trackDocumentView(docId, siteIdToUse);
            const documentRow = documents.find(d => d.id === docId) || {};
            const primaryMachine = Array.isArray(documentRow.machine_details)
                ? documentRow.machine_details[0] || {}
                : {};
            const documentName =
                documentRow.name ||
                documentRow.document_name ||
                documentRow.title ||
                documentRow.filename ||
                "";
            trackEvent("document_viewed", {
                document_id: docId,
                document_name: documentName,
                site_id: siteIdToUse || "",
                machine_id: documentRow.machine_id || primaryMachine.machine_id || "",
                machine_name: documentRow.machine_name || primaryMachine.machine_name || "",
                machine_number: documentRow.machine_number || primaryMachine.machine_number || "",
                path: window.location.pathname || "/documentation",
                page_path: window.location.pathname || "/documentation",
                action_category: "documents",
                element_label: `View document ${documentName || docId}`,
            });
            console.log(' Document view tracked successfully:', docId);
            return true;
        } catch (error) {
            console.error('Error tracking document view:', error);
            return false;
        }
    };


    /**
     * CORRECTED: Single file download using backend proxy
     * CRITICAL FIX: downloadDocumentProxy returns a Blob directly, NOT a Response object
     */
    const handleDownload = async (doc) => {
        try {
            const filename = getDocumentDownloadFilename(doc);

            // Show immediate toast for download starting
            toast.trigger({
                message: t('MESSAGES.DOWNLOAD_STARTING_SINGLE', { filename }) || `Starting download for ${filename}`,
                severity: 'info'
            });

            setSingleDownloading(doc.id);

            // Use backend proxy for all file types
            if (doc.id && (doc.blob_path || (doc.attachment_type === 'file' && doc.attachment))) {


                // CRITICAL FIX: downloadDocumentProxy returns blob directly
                // handleRequest with responseType: "blob" automatically extracts the blob
                const blob = await getDocumentDownloadBlob(doc);


                // Create download link
                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = filename;
                link.style.display = 'none';

                // Trigger download
                document.body.appendChild(link);
                link.click();
                trackDocumentDownloadEvent(doc);

                // Cleanup
                setTimeout(() => {
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(blobUrl);
                    setSingleDownloading(null);

                    // Show success toast immediately after download completes
                    toast.trigger({
                        message: t('MESSAGES.DOWNLOAD_SUCCESS') || 'Download successful',
                        severity: 'success'
                    });
                }, 100);
                return true;
            }

            // Handle URL type documents
            if (doc.attachment_type === 'url' && doc.attachment) {

                window.open(doc.attachment, '_blank');
                trackDocumentDownloadEvent(doc);
                setSingleDownloading(null);

                // Show success toast for URL documents
                toast.trigger({
                    message: t('MESSAGES.DOWNLOAD_SUCCESS') || 'Document opened successfully',
                    severity: 'success'
                });
                return true;
            }

            // Fallback error
            setSingleDownloading(null);
            toast.trigger({
                message: t('MESSAGES.NO_ATTACHMENT') || 'No attachment available',
                severity: 'warning'
            });
            return false;

        } catch (error) {
            console.error(' Error downloading document:', error);
            setSingleDownloading(null);
            toast.trigger({
                message: t('MESSAGES.DOWNLOAD_ERROR') || 'Download failed',
                severity: 'error'
            });
            return false;
        }
    };

    /**
     * CORRECTED: Bulk download function
     * CRITICAL FIX: downloadDocumentProxy returns a Blob directly, NOT a Response object
     */
    const handleBulkDownload = async () => {
        if (selectedDocsData.length === 0) {
            showSnackbar(t('MESSAGES.NO_DOCUMENTS_SELECTED') || 'No documents selected', 'warning');
            return;
        }

        if (selectedDocsData.length > MAX_BULK_DOWNLOAD) {
            showSnackbar(
                t('MESSAGES.MAX_DOWNLOAD_LIMIT', { max: MAX_BULK_DOWNLOAD }) ||
                `You can download a maximum of ${MAX_BULK_DOWNLOAD} files at once.`,
                'error'
            );
            return;
        }

        // Strict category validation
        if (!areSameCategorySelected) {
            showSnackbar(
                t('MESSAGES.DIFFERENT_CATEGORY_ERROR') ||
                'All selected documents must be from the same category.',
                'error'
            );
            return;
        }

        setDownloading(true);

        const downloadQueue = [...selectedDocsData];
        let successCount = 0;
        let failCount = 0;

        showSnackbar(
            t('MESSAGES.DOWNLOAD_STARTING', { count: downloadQueue.length }) ||
            `Preparing to download ${downloadQueue.length} file(s)...`,
            'info'
        );

        // Download files sequentially
        for (let i = 0; i < downloadQueue.length; i++) {
            const doc = downloadQueue[i];
            const queuePosition = `${i + 1}/${downloadQueue.length}`;

            try {

                // Use backend proxy for all files
                if (doc.id && (doc.blob_path || (doc.attachment_type === 'file' && doc.attachment))) {

                    // CRITICAL FIX: downloadDocumentProxy returns blob directly
                    const blob = await getDocumentDownloadBlob(doc);


                    // Create temporary blob URL
                    const blobUrl = window.URL.createObjectURL(blob);

                    // Create download link
                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.download = getDocumentDownloadFilename(doc, `document_${i + 1}`);
                    link.style.display = 'none';

                    // Trigger download
                    document.body.appendChild(link);
                    link.click();
                    trackDocumentDownloadEvent(doc);

                    // Cleanup after a short delay to ensure download starts
                    setTimeout(() => {
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(blobUrl);
                    }, 100);

                    successCount++;
                }
                // URL type
                else if (doc.attachment_type === 'url' && doc.attachment) {
                    window.open(doc.attachment, '_blank');
                    trackDocumentDownloadEvent(doc);
                    successCount++;
                }
                else {
                    console.warn(` No valid download method for: "${doc.name}"`);
                    failCount++;
                }

                // Show progress every 3 files
                if ((i + 1) % 3 === 0 && i + 1 < downloadQueue.length) {
                    showSnackbar(
                        `Downloaded ${i + 1} of ${downloadQueue.length} files...`,
                        'info'
                    );
                }

                // Delay between downloads to ensure each completes
                if (i < downloadQueue.length - 1) {
                    const delayMs = 800;
                    await new Promise(resolve => setTimeout(resolve, delayMs));
                }

            } catch (error) {
                console.error(` Queue [${queuePosition}] FAILED: "${doc.name}"`);
                console.error(`     Error:`, error.message);
                failCount++;
            }
        }

        setDownloading(false);

        if (failCount === 0) {
            showSnackbar(
                t('MESSAGES.BULK_DOWNLOAD_SUCCESS', { count: successCount }) ||
                `Successfully downloaded ${successCount} file(s).`,
                'success'
            );
        } else {
            showSnackbar(
                t('MESSAGES.BULK_DOWNLOAD_PARTIAL', { success: successCount, failed: failCount }) ||
                `Downloaded ${successCount} file(s). ${failCount} file(s) failed.`,
                'warning'
            );
        }

        // Clear selection after download
        onSelectDocument([]);
    };

    const handleView = useCallback(async (doc, fromButton = false) => {

        try {
            //  FIX: Ensure we have a valid siteId
            const siteIdToUse = selectedSiteId || doc.site_id;

            if (!siteIdToUse) {
                console.error(' No siteId available for tracking view');
            } else {
                const tracked = await handleTrackView(doc.id);
                if (tracked && onFavoriteToggled) {
                    onFavoriteToggled(doc);
                }
                if (!tracked) {
                    console.warn('View tracking failed, but continuing to open document');
                }
            }

            setActiveDocument(doc);
            setViewModalOpen(true);

            if (onFavoriteToggled) {
                await onFavoriteToggled();
            }
        } catch (error) {
            console.error('Error viewing document:', error);
            showSnackbar(t('MESSAGES.VIEW_ERROR') || 'Failed to view document', 'error');
        }
    }, [selectedSiteId, onFavoriteToggled, t, showSnackbar]);

    const handleModalClose = () => {
        setViewModalOpen(false);
        setActiveDocument(null);
    };

    // Opens documentToOpen through the exact same handleView() a manual row click uses,
    // so a CHIP-driven "open document xyz" request produces identical behavior (view
    // tracking, favorites refresh, modal) to clicking the document in the table. Guarded by
    // ID so re-renders/prop identity changes for the SAME document don't reopen the modal.
    const lastOpenedDocumentIdRef = useRef(null);
    useEffect(() => {
        if (!documentToOpen || documentToOpen.id === lastOpenedDocumentIdRef.current) return;
        lastOpenedDocumentIdRef.current = documentToOpen.id;
        handleView(documentToOpen, false);
        if (onDocumentOpened) onDocumentOpened();
    }, [documentToOpen, handleView, onDocumentOpened]);

    const isDocumentNew = (updatedAt) => {
        if (!updatedAt) return false;
        try {
            const updatedDate = new Date(updatedAt);
            const threeMonthsAgo = new Date();
            threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
            return updatedDate >= threeMonthsAgo;
        } catch (err) {
            console.error('Error parsing updated date:', err);
            return false;
        }
    };

    const getFileExtension = (filename) => {
        if (!filename) return '';
        const parts = filename.split('.');
        return parts.length > 1 ? parts.pop().toLowerCase() : '';
    };

    const formatDate = (dateString) => {
        if (!dateString) return t('LABELS.NA');
        try {
            const date = new Date(dateString);
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const year = date.getFullYear();
            return `${month}-${day}-${year}`;
        } catch (err) {
            return t('LABELS.NA');
        }
    };

    const getMachineInfo = (doc) => {
        if (!doc.machine_details || doc.machine_details.length === 0) {
            return t('LABELS.ALL_MACHINES');
        }
        const firstMachine = doc.machine_details[0];
        const machineLabel = `${firstMachine.machine_number}`;
        if (doc.machine_details.length === 1) {
            return machineLabel;
        }
        return `${machineLabel} (+${doc.machine_details.length - 1})`;
    };

    const getMachineTooltip = (doc) => {
        if (!doc.machine_details || doc.machine_details.length === 0) {
            return t('TOOLTIPS.AVAILABLE_ALL_MACHINES');
        }
        return doc.machine_details
            .map(m => `${m.machine_number}`)
            .join('\n');
    };

    // Column definitions remain the same as in your original code
    const columns = useMemo(() => {
        const normalizedCurrentCategory = normalizeDocumentCategoryKey(currentCategory);
        const preferFilenameFirst =
            normalizedCurrentCategory === 'mechanical-drawings' ||
            normalizedCurrentCategory === 'controls-drawings';

        let baseColumns = [
            {
                field: 'name',
                headerName: preferFilenameFirst
                    ? 'File Number'
                    : t('LABELS.FILENAME'),
                flex: 3,
                minWidth: 250,
                hideable: false,
                sortable: true,
                filterable: false,
                renderCell: (params) => {
                    const isNew = isDocumentNew(params.row.updated_at);
                    const fileExtension = getFileExtension(params.row.name);

                    return (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                width: '100%',
                                height: '100%',
                                py: 1,
                                cursor: 'pointer'
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleView(params.row, false);
                            }}
                        >
                            <Box sx={{ flexShrink: 0 }}>
                                <DocCardImage
                                    filename={params.row.name}
                                    path={params.row.attachment}
                                    fileExtension={fileExtension}
                                />
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    overflow: 'hidden',
                                    minWidth: 0,
                                    flex: 1
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontSize: '0.875rem',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        color: 'text.primary'
                                    }}
                                >
                                    {String(params.value || '').replace(/\.html$/i, '')}
                                </Typography>

                                {isNew && (
                                    <Chip
                                        label={t('LABELS.NEW')}
                                        size="small"
                                        sx={{
                                            height: 18,
                                            fontSize: '0.65rem',
                                            fontWeight: 600,
                                            backgroundColor: mainTheme => mainTheme.palette.primary.main,
                                            color: '#ffffff',
                                            flexShrink: 0
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>
                    );
                }
            }
        ];

        const hasOthersDocuments = documents.some(doc => isOthersCategory(doc.category));

        if (hasOthersDocuments) {
            baseColumns.splice(1, 0, {
                field: 'document_type',
                headerName: t('LABELS.TYPE') || 'Type',
                flex: 2,
                minWidth: 200,
                sortable: true,
                filterable: false,
                valueGetter: (value, row) => row.document_type || '',
                renderCell: (params) => {
                    if (!isOthersCategory(params.row.category) || !params.value) {
                        return (
                            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        fontSize: '0.875rem',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        width: '100%'
                                    }}
                                >
                                    —
                                </Typography>
                            </Box>
                        );
                    }

                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
                            <Tooltip title={params.value} arrow placement="top">
                                <Typography
                                    variant="body2"
                                    color="text.primary"
                                    sx={{
                                        fontSize: '0.875rem',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        width: '100%'
                                    }}
                                >
                                    {params.value}
                                </Typography>
                            </Tooltip>
                        </Box>
                    );
                }
            });
        }

        // Only drawing filenames carry a part number, so this column appears only
        // when at least one row on screen actually resolved to an object. Rows that
        // did not stay blank rather than falling back to the filename.
        const hasObjectNames = documents.some(doc => doc.object_name);

        if (hasObjectNames) {
            const objectNameColumn = {
                field: 'object_name',
                headerName: preferFilenameFirst
                    ? 'File Name'
                    : t('TABLE_HEADERS.OBJECT_NAME', { defaultValue: 'Document Name' }),
                flex: 2,
                minWidth: 200,
                sortable: true,
                filterable: false,
                renderCell: (params) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
                        <Tooltip title={params.value || ''} arrow placement="top">
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    fontSize: '0.875rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    width: '100%'
                                }}
                            >
                                {params.value || ''}
                            </Typography>
                        </Tooltip>
                    </Box>
                )
            };

            if (preferFilenameFirst) {
                baseColumns.splice(1, 0, objectNameColumn);
            } else {
                baseColumns.unshift(objectNameColumn);
            }
        }

        const remainingColumns = [
            {
                field: 'machine_name',
                headerName: t('TABLE_HEADERS.MACHINE_NAME'),
                flex: 2,
                minWidth: 200,
                sortable: true,
                filterable: false,
                valueGetter: (value, row) => {
                    if (!row.machine_details || row.machine_details.length === 0) {
                        return t('LABELS.ALL_MACHINES');
                    }
                    return row.machine_details[0].machine_name;
                },
                sortComparator: (v1, v2) => {
                    const allMachinesText = t('LABELS.ALL_MACHINES');
                    if (v1 === allMachinesText && v2 === allMachinesText) return 0;
                    if (v1 === allMachinesText) return 1;
                    if (v2 === allMachinesText) return -1;
                    return v1.localeCompare(v2, undefined, { sensitivity: 'base' });
                },
                renderCell: (params) => {
                    const doc = params.row;

                    if (!doc.machine_details || doc.machine_details.length === 0) {
                        return (
                            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        fontSize: '0.875rem',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        width: '100%'
                                    }}
                                >
                                    {t('LABELS.ALL_MACHINES')}
                                </Typography>
                            </Box>
                        );
                    }

                    const firstMachineName = doc.machine_details[0].machine_name;
                    const machineCount = doc.machine_details.length;
                    const tooltip = doc.machine_details
                        .map(m => `${m.machine_name}`)
                        .join('\n');

                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
                            <Tooltip title={tooltip} arrow placement="top">
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        fontSize: '0.875rem',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        width: '100%'
                                    }}
                                >
                                    {machineCount === 1
                                        ? firstMachineName
                                        : `${firstMachineName} (+${machineCount - 1})`
                                    }
                                </Typography>
                            </Tooltip>
                        </Box>
                    );
                }
            },
            {
                field: 'machine_details',
                headerName: t('LABELS.MACHINE_NUMBER'),
                flex: 1,
                minWidth: 150,
                sortable: true,
                filterable: false,
                sortComparator: (v1, v2, param1, param2) => {
                    if (!param1 || !param1.api || !param1.id) return 0;
                    if (!param2 || !param2.api || !param2.id) return 0;

                    const row1 = param1.api.getRow(param1.id);
                    const row2 = param2.api.getRow(param2.id);

                    if (!row1 || !row2) return 0;

                    const machine1 = getMachineInfo(row1);
                    const machine2 = getMachineInfo(row2);

                    const allMachinesText = t('LABELS.ALL_MACHINES');
                    if (machine1 === allMachinesText && machine2 === allMachinesText) return 0;
                    if (machine1 === allMachinesText) return 1;
                    if (machine2 === allMachinesText) return -1;

                    return machine1.localeCompare(machine2, undefined, { numeric: true, sensitivity: 'base' });
                },
                renderCell: (params) => {
                    const machineInfo = getMachineInfo(params.row);
                    const tooltip = getMachineTooltip(params.row);

                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
                            <Tooltip title={tooltip} arrow placement="top">
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        fontSize: '0.875rem',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        width: '100%'
                                    }}
                                >
                                    {machineInfo}
                                </Typography>
                            </Tooltip>
                        </Box>
                    );
                }
            },
            {
                field: 'uploaded_at',
                headerName: t('LABELS.CREATED_DATE'),
                flex: 1,
                minWidth: 150,
                sortable: true,
                filterable: false,
                renderCell: (params) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                            {formatDate(params.value)}
                        </Typography>
                    </Box>
                )
            },
            {
                field: 'updated_at',
                headerName: t('LABELS.MODIFIED_DATE'),
                flex: 1,
                minWidth: 150,
                sortable: true,
                filterable: false,
                renderCell: (params) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                            {formatDate(params.value)}
                        </Typography>
                    </Box>
                )
            },
            {
                field: 'actions',
                headerName: t('LABELS.ACTIONS'),
                flex: 1,
                minWidth: 120,
                headerAlign: 'center',
                align: 'center',
                sortable: false,
                filterable: false,
                renderCell: (params) => {
                    const isFavorite = favorites.includes(params.row.id);

                    return (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 0.5,
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100%',
                                width: '100%'
                            }}
                        >
                            {/* Favorite icon — hidden when sidebar is hidden */}
                            {!hideFavoriteIcon && (
                                <Tooltip
                                    title={
                                        isFavorite
                                            ? t('TOOLTIPS.REMOVE_FROM_FAVORITES')
                                            : t('TOOLTIPS.ADD_TO_FAVORITES')
                                    }
                                    arrow
                                >
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleToggleFavorite(
                                                params.row.id,
                                                isFavorite,
                                                params.row.site_id
                                            );
                                        }}
                                        sx={{
                                            color: isFavorite ? '#f44336' : 'text.secondary',
                                            '&:hover': {
                                                color: isFavorite ? '#f44336' : 'primary.main'
                                            },
                                            p: 0.5
                                        }}
                                    >
                                        {isFavorite ? (
                                            <StarIcon sx={{ fontSize: '1.1rem' }} />
                                        ) : (
                                            <StarBorderIcon sx={{ fontSize: '1.1rem' }} />
                                        )}
                                    </IconButton>
                                </Tooltip>
                            )}

                            {/* 👁 View */}
                            <Tooltip title={t('TOOLTIPS.VIEW_DOCUMENT')} arrow>
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleView(params.row, true);
                                    }}
                                    sx={{
                                        color: 'text.secondary',
                                        '&:hover': { color: 'primary.main' },
                                        p: 0.5
                                    }}
                                >
                                    <VisibilityIcon sx={{ fontSize: '1.1rem' }} />
                                </IconButton>
                            </Tooltip>

                            {/* ⬇ Download */}
                            <Tooltip
                                title={isHtmlManual(params.row)
                                    ? t('TOOLTIPS.EXPORT_PDF', { defaultValue: 'Export as PDF' })
                                    : t('TOOLTIPS.DOWNLOAD_DOCUMENT')}
                                arrow
                            >
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDownload(params.row);
                                    }}
                                    disabled={singleDownloading === params.row.id}
                                    sx={{
                                        color: 'text.secondary',
                                        '&:hover': { color: 'primary.main' },
                                        p: 0.5
                                    }}
                                >
                                    {singleDownloading === params.row.id ? (
                                        <CircularProgress size={20} />
                                    ) : isHtmlManual(params.row) ? (
                                        <PictureAsPdfIcon sx={{ fontSize: '1.1rem' }} />
                                    ) : (
                                        <DownloadIcon sx={{ fontSize: '1.1rem' }} />
                                    )}
                                </IconButton>
                            </Tooltip>
                        </Box>
                    );
                }

            }
        ];

        const allColumns = [...baseColumns, ...remainingColumns];
        const roleId = userRole?.role_id;
        const shouldHideCreatedDate = roleId === 3 || roleId === 4;

        if (shouldHideCreatedDate) {
            return allColumns.filter(col => col.field !== 'uploaded_at');
        }

        return allColumns;
    }, [t, favorites, selectedSiteId, handleView, userRole, documents]);

    const customButtons = useMemo(() => {
        const buttons = [];

        if (canAddDocument) {
            buttons.push({
                label: t('BUTTON.ADD_DOCUMENT', { defaultValue: 'Add Document' }),
                icon: <AddCircleOutlineIcon />,
                onClick: handleOpenUploadModal,
                disabled: uploadSubmitting || categorySubmitting || deleteSubmitting || machineReassignSubmitting,
            });
        }

        if (selectedDocuments.length > 0) {
            if (canUpdateCategory) {
                buttons.push({
                    label: t('BUTTON.UPDATE_CATEGORY', { defaultValue: 'Update Category' }),
                    icon: <CategoryIcon />,
                    onClick: handleOpenCategoryModal,
                    disabled: categorySubmitting || !areSameCategorySelected,
                });
            }

            if (canChangeMachine) {
                buttons.push({
                    label: t('BUTTON.CHANGE_MACHINE', { defaultValue: 'Change Machine' }),
                    icon: <DriveFileMoveIcon />,
                    onClick: handleOpenMachineReassignModal,
                    disabled: machineReassignSubmitting || categorySubmitting || !areSameCategorySelected,
                });
            }

            if (canDeleteDocuments) {
                buttons.push({
                    label: deleteSubmitting
                        ? t('BUTTON.DELETING', { defaultValue: 'Deleting...' })
                        : t('BUTTON.DELETE_SELECTED', { defaultValue: 'Delete Selected' }),
                    icon: <DeleteIcon />,
                    onClick: handleDeleteSelectedDocuments,
                    disabled: deleteSubmitting || categorySubmitting || downloading,
                });
            }

            if (canDownloadSelectedDocuments) {
                const buttonLabel = downloading ? t('BUTTONS.DOWNLOADING') || 'Downloading...' : t('BUTTONS.DOWNLOAD_SELECTED', { count: selectedDocuments.length }) || `Download Selected (${selectedDocuments.length})`;

                buttons.push({
                    label: buttonLabel,
                    icon: <FileDownloadIcon />,
                    onClick: handleBulkDownload,
                    disabled: downloading || !areSameCategorySelected
                });
            }
        }

        if (canDeleteDocuments && bulkDeleteMachineNumber) {
            buttons.push({
                label: deleteSubmitting
                    ? t('BUTTON.DELETING', { defaultValue: 'Deleting...' })
                    : 'Delete All for Machine',
                icon: <DeleteIcon />,
                onClick: handleDeleteAllForMachine,
                disabled: deleteSubmitting || categorySubmitting || downloading,
            });
        }

        return buttons;
    }, [
        selectedDocuments,
        canAddDocument,
        canUpdateCategory,
        canChangeMachine,
        canDeleteDocuments,
        canDownloadSelectedDocuments,
        uploadSubmitting,
        categorySubmitting,
        deleteSubmitting,
        machineReassignSubmitting,
        downloading,
        areSameCategorySelected,
        t,
        handleOpenUploadModal,
        handleOpenCategoryModal,
        handleOpenMachineReassignModal,
        handleDeleteSelectedDocuments,
        handleDeleteAllForMachine,
        handleBulkDownload,
        bulkDeleteMachineNumber,
    ]);

    if (loading) {
        return <DatagridLoading columns={6} rows={8} />;
    }

    return (
        <>
            <Datagrid
                data={documents}
                columns={columns}
                rowIdField="id"
                checkboxSelection={true}
                onSelect={handleSelection}
                autoHeight={true}
                gridHeight={600}
                showSearchBar={false}
                handleSearch={handleSearch}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={handleColumnVisibilityChange}
                customButtons={customButtons}
                rowSelectionModel={selectedDocuments}
                disableSelectAll={true}
                localeText={localeText}

                // ADD SERVER-SIDE PAGINATION PROPS:
                paginationMode={paginationMode}
                rowCount={rowCount}
                paginationModel={paginationModel}
                onPaginationModelChange={onPaginationModelChange}
                pageSizeOptions={[5, 10, 25, 50]}
            />

            <DocumentViewModal
                open={viewModalOpen}
                setOpen={setViewModalOpen}
                document={activeDocument}
                onClose={handleModalClose}
            />

            <AdminCategoryUpdateModal
                open={categoryModalOpen}
                onClose={handleCloseCategoryModal}
                documents={selectedDocsData}
                categories={availableTargetCategories}
                loadingCategories={categoriesLoading}
                categoriesReady={categoriesReady}
                sourceCategoryLabel={selectedCategorySource?.name || currentCategory || 'Uncategorized'}
                targetCategory={targetCategory}
                onTargetCategoryChange={setTargetCategory}
                onLoadCategories={handleLoadCategoriesForAssignment}
                onCategorySearchChange={handleCategorySearchChange}
                onSubmit={handleSubmitCategoryUpdate}
                submitting={categorySubmitting}
                onRemoveDocument={handleRemoveDocumentFromSelection}
            />

            <AdminDocumentUploadModal
                open={uploadModalOpen}
                onClose={handleCloseUploadModal}
                dropzoneKey={uploadFormResetKey}
                formData={uploadFormData}
                setFormData={setUploadFormData}
                machineOptions={machineOptions}
                machineLoading={machineOptionsLoading}
                selectedMachine={uploadSelectedMachine}
                onMachineChange={(value) => {
                    setUploadSelectedMachine(value);
                    setUploadError('');
                }}
                onMachineSearchChange={handleMachineSearchChange}
                categoryOptions={categoryOptions}
                categoriesLoading={categoriesLoading}
                selectedCategory={uploadSelectedCategory}
                onCategoryChange={(value) => {
                    setUploadSelectedCategory(value);
                    setUploadError('');
                }}
                onCategorySearchChange={handleCategorySearchChange}
                onSubmit={handleSubmitUploadDocument}
                submitting={uploadSubmitting}
                errorMessage={uploadError}
            />

            <AdminMachineReassignModal
                open={machineReassignModalOpen}
                onClose={handleCloseMachineReassignModal}
                documents={selectedDocsData}
                currentCategoryLabel={selectedCategorySource?.name || currentCategory || 'Uncategorized'}
                machineOptions={machineOptions}
                machineLoading={machineOptionsLoading}
                selectedMachine={targetMachineSelection}
                onMachineChange={(value) => {
                    setTargetMachineSelection(value);
                    setMachineReassignError('');
                }}
                onMachineSearchChange={handleMachineSearchChange}
                onSubmit={handleSubmitMachineReassign}
                submitting={machineReassignSubmitting}
                errorMessage={machineReassignError}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default DocumentationTable;
